import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { tokens } from "../../theme";
import { foyerApi, type OwnerShell } from "../../http/foyer";
import { PageHeader } from "../../components/PageHeader";
import type { ContentDeal } from "@shared/content-deal";
import type { ContentStatement } from "@shared/content-statement";
import {
  StatementView,
  dayOf,
  money,
} from "../../components/revenue/StatementView";
import { useFormat } from "../../i18n/format";

/**
 * Revenue — what a supplier earned, and what is still owed (`WL-6`).
 *
 * ## Two questions, and they are not the same question
 *
 * `GET /owner/statement` answers *what did I earn in a period* — recognised
 * money, from deals that reached PAID, from storefront orders and from the
 * owner's titles sold on ANY purchase portal (one line per title, consumer
 * TVOD R15/R16), with Nenda's share separated out. The statement itself is
 * drawn by `StatementView`, which staff see too. `GET /owner/deals` answers *where is each piece of
 * business* — draft, offered, accepted, invoiced, paid. A page that showed only
 * the first would be a supplier's accounts; a page that showed only the second
 * would be their pipeline. They want both, side by side, and the gap between
 * them is exactly the conversation they want to have with us.
 *
 * ## ⚠️ The default period is ONE MONTH, and it says so
 *
 * With no dates the server answers for the **last full calendar month**, not
 * for all time. A screen that printed that figure under a bare "Revenue"
 * heading would be reporting one month as the lifetime total, and nobody would
 * catch it because the number is plausible. The period is on the page, in
 * words, and it is editable.
 *
 * ## `revenueShareBps` is null until it is agreed
 *
 * Not zero. A statement whose share is unagreed shows the gross and refuses to
 * invent a split — printing "Nenda's share: 0" would be a commitment neither
 * side made.
 *
 * ## Overdue is asked for separately
 *
 * `GET /owner/deals/overdue` applies a grace period the client does not model,
 * so the answer comes from the server rather than from filtering the deal list
 * by a date this page guessed at.
 */

interface ShellContext {
  shell: OwnerShell;
}

const DEAL_STATES = [
  "draft",
  "offered",
  "accepted",
  "invoiced",
  "paid",
  "cancelled",
] as const;
type DealState = (typeof DEAL_STATES)[number];
const isDealState = (s: string): s is DealState =>
  (DEAL_STATES as readonly string[]).includes(s);

export default function SupplierRevenuePage() {
  useOutletContext<ShellContext>();
  const { t } = useTranslation("supplier");
  const fmt = useFormat();
  const [statement, setStatement] = useState<ContentStatement | null>(null);
  const [deals, setDeals] = useState<ContentDeal[] | null>(null);
  const [overdue, setOverdue] = useState<ContentDeal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<{ from: string; to: string } | null>(null);

  const load = useCallback(
    async (asked?: { from: string; to: string }) => {
      try {
        const [s, d, o] = await Promise.all([
          foyerApi.ownerStatement(asked),
          foyerApi.ownerDeals(),
          // Its own call: the grace period is the server's rule, and filtering
          // the deal list here would be this page inventing a second one.
          foyerApi.ownerOverdueDeals(),
        ]);
        setStatement(s);
        setDeals(d);
        setOverdue(o);
        // The server decides the default window; echo back what it used rather
        // than what we asked for.
        setRange({ from: dayOf(s.from), to: dayOf(s.to) });
        setError(null);
      } catch (e) {
        const err = e as { code?: string; message: string };
        setError(
          err.code === "invalid_period"
            ? t("supplierRevenuePage.invalidPeriod")
            : err.message
        );
      }
    },
    [t]
  );

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!statement || !deals)
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={26} />
      </Box>
    );

  const panel = {
    background: tokens.surface,
    border: `1px solid ${tokens.divider}`,
    borderRadius: 2,
    p: 2.5,
  };

  return (
    <Stack spacing={3}>
      <PageHeader
        eyebrow={t("supplierRevenuePage.eyebrow")}
        title={t("supplierRevenuePage.title")}
        subtitle={t("supplierRevenuePage.subtitle")}
      />

      {overdue.length > 0 ? (
        <Alert severity="warning" data-testid="revenue-overdue">
          {t("supplierRevenuePage.overdue", { count: overdue.length })}
        </Alert>
      ) : null}

      <StatementView
        statement={statement}
        range={range}
        onRangeChange={setRange}
        onApply={(r) => void load(r)}
        csvName="statement"
      />

      <Box sx={panel}>
        <Typography sx={{ fontWeight: "fontWeightSemibold", mb: 1.5 }}>
          {t("supplierRevenuePage.yourDeals")}
        </Typography>
        {deals.length === 0 ? (
          <Typography sx={{ color: tokens.inkMuted }}>
            {t("supplierRevenuePage.noDeals")}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {t("supplierRevenuePage.columns.customer")}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {t("supplierRevenuePage.columns.titles")}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {t("supplierRevenuePage.columns.price")}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {t("supplierRevenuePage.columns.state")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deals.map((deal) => (
                  <TableRow key={deal.id} data-testid={`deal-${deal.id}`}>
                    <TableCell>
                      {deal.customerName}
                      <Typography
                        variant="caption"
                        sx={{ color: tokens.inkFaint, display: "block" }}
                      >
                        {t("supplierRevenuePage.customerLine", {
                          kind: deal.customerKind,
                          country: deal.customerCountry,
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>{deal.assetIds.length}</TableCell>
                    <TableCell>
                      {money(fmt, deal.price.amount, deal.price.currency)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          isDealState(deal.state)
                            ? t(`supplierRevenuePage.state.${deal.state}`)
                            : deal.state
                        }
                        color={deal.state === "paid" ? "primary" : "default"}
                        variant={deal.state === "paid" ? "filled" : "outlined"}
                        sx={{ height: 20, typography: "micro" }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Stack>
  );
}
